import moment from 'moment';
import { entityStore } from '~/utility/BinSentry-ui-utility';
import '../../bins/parts/bin-path-name';
import '../../bins/parts/bin-profile';
import '../../common/bn-card';
import '../../common/bn-flash';
import '../../common/bn-icon';
import '../../common/bn-loading';
import '../../common/bn-logo-shield';
import '../../common/bn-property';
import './work-order-header';
import { FIRMWARE_MODE } from '../../devices/props/device-firmware-mode';
import { LitEntity, html, css, renderIf } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';

const INTERVAL = 5000;

class WorkOrderCard extends LitEntity {
  static get properties() {
    return {
      deviceHref: { type: String, attribute: false },
      deviceMode: { type: String, attribute: false },
      latestEventHref: { type: String, attribute: false },
      latestEvent: { type: Object, attribute: false },
      hideAddress: { type: Boolean, attribute: 'hide-address' },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
      }
      .body {
        position: relative;
        display: flex;
        align-items: stretch;
      }
      .properties {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
      }
      .status {
        width: 100px;
        max-height: 300px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 10px;
        background: var(--gray-20);
      }
      .status bn-icon {
        font-size: 40px;
        color: var(--blue-60);
      }
      .status bn-icon.error {
        color: var(--red-20);
      }
      .status bn-logo-shield {
        height: 40px;
        width: 40px;
        --logo-fill-colour: var(--primary);
      }
      bn-property {
        margin: .5em 1em 0 0;
        white-space: unset;
      }
      bn-property[label=State] {
        font-weight: bold;
      }
      bn-property pre {
        margin: 0;
        white-space: pre-wrap;
        min-height: 1em;
        font-family: var(--content-font);
      }
    `];
  }

  render() {
    if (!this.entity) {
      return html`
        <bn-card>
          <bn-loading></bn-loading>
        </bn-card>
      `;
    }

    const {
      location,
      state,
      notes,
      preDiagnosis,
      scheduledFor,
      completedAt,
      requestType,
    } = this.entity.properties;

    return html`
      <lit-entity .href="${this.deviceHref}" @update="${this._onUpdateDevice}"></lit-entity>
      <lit-entity .href="${this.latestEventHref}" @update="${this._onUpdateLatestEvent}"></lit-entity>

      <bn-card .href="/work-orders/confirm?workOrderId=${encodeURIComponent(this.href)}">
        <work-order-header .href="${this.href}"></work-order-header>

        ${renderIf(!this.hideAddress, () => html`
          <div class="address">
            <bn-property stacked label="Address">${location && location.address}</bn-property>
          </div>
        `)}

        <div class="body">
          <div class="properties">
            <bn-property stacked label="Type">${requestType}</bn-property>
            <bn-property stacked label="State">${state}</bn-property>
            ${renderIf(scheduledFor, () => html`
              <bn-property stacked label="Scheduled For">${this._formatDate(scheduledFor)}</bn-property>
            `)}
            ${renderIf(state === 'completed', () => html`
              <bn-property stacked label="Completed">${this._formatDateTime(completedAt)}</bn-property>
            `)}
            ${renderIf(this.deviceHref, () => html`
              <bn-property stacked label="Device Mode">
                <device-firmware-mode .href="${this.deviceHref}"></device-firmware-mode>
              </bn-property>
            `)}
            ${renderIf(requestType === 'service', () => html`
              <bn-property stacked label="Pre-Diagnosis"><pre>${preDiagnosis}</pre></bn-property>
            `)}
            ${renderIf(notes, () => html`
              <bn-property stacked label="Notes"><pre>${notes}</pre></bn-property>
            `)}
            ${renderIf(this.latestEvent, () => html`
             <bn-property stacked label="Latest Reading">
                <device-event-payload-type payload-type="${this.latestEvent.properties.payloadType}"></device-event-payload-type>
                ${renderIf(this.latestEvent.properties.sensorCriticalError, () => html`
                  (${this.latestEvent.properties.sensorCriticalError.title})
                `)}
              </bn-property>
            `)}
          </div>

          <div class="status">
            ${this._renderStatus()}
          </div>
        </div>

        <bn-flash></bn-flash>
      </bn-card>
    `;
  }

  _renderStatus() {
    const { state, completedAt, requestType } = this.entity.properties;
    if (state === 'completing') {
      return html`<bn-loading></bn-loading>`;
    }
    if (state === 'error') {
      return html`<bn-icon name="far fa-exclamation-triangle" class="error"></bn-icon>`;
    }
    if (state === 'completed' && requestType !== 'removal') {
      if (this.deviceMode !== FIRMWARE_MODE.RUN) {
        return html`<bn-loading></bn-loading>`;
      }
      return html`
        <bin-profile
          .href="${this.getHref('https://api.binsentry.com/rel/bin')}"
          .levelfreshnessdate="${completedAt}"
        ></bin-profile>
      `;
    }
    return html`<bn-logo-shield></bn-logo-shield>`;
  }

  async _entityChanged(entity) {
    clearTimeout(this._timeoutHandle);

    super._entityChanged(entity);

    this.binHref = this.getHref('https://api.binsentry.com/rel/bin');
    this.deviceHref = this.entity?.getSubEntityByClasses(['device', 'active'])?.href;

    if (!this.entity) {
      return;
    }

    const isComplete = await this._checkWorkOrder(this.entity);
    if (!isComplete) {
      this._timeoutHandle = setTimeout(() => entityStore.invalidateHref(this.href), INTERVAL);
    }
  }

  _onUpdateDevice(e) {
    const { entity } = e.detail;
    this.deviceMode = this.getProperty('firmwareMode.reported', undefined, entity);
    this.latestEventHref = this.getHref('https://api.binsentry.com/rel/event-latest', entity);
  }

  _onUpdateLatestEvent(e) {
    this.latestEvent = e.detail?.entity;
  }

  _problem(error) {
    this.dispatchEvent(new CustomEvent('problem', { detail: { href: this.href, error }, bubbles: true, composed: true }));
    this.shadowRoot.querySelector('bn-flash')?.critical(error);
    return false;
  }

  async _checkWorkOrder(workOrder) {
    this.shadowRoot.querySelector('bn-flash')?.clear();

    const activeDeviceLink = workOrder.getSubEntityByClasses(['device', 'active']);

    const { requestType, completedAt, state } = workOrder.properties;

    if (state !== 'completed') {
      return true;
    }

    if (!['service', 'removal', 'installation'].includes(requestType)) {
      return this._problem(`Unknown request type: ${requestType}`);
    }

    if (requestType === 'removal') {
      return activeDeviceLink ? this._problem('Active device present on removal request') : true;
    }

    if (['service', 'installation'].includes(requestType) && !activeDeviceLink) {
      return this._problem('No active device present');
    }

    const activeDevice = await this.fetchSiren(activeDeviceLink.href);
    const deviceMode = activeDevice.properties.firmwareMode.reported;

    if (deviceMode !== FIRMWARE_MODE.RUN) {
      return this._problem('Device is not in run mode');
    }

    const binLink = workOrder.getSubEntityByClass('bin');
    const binEntity = await this.fetchSiren(binLink.href);

    const latestLevelLink = binEntity.getSubEntityByRel('https://api.binsentry.com/rel/bin-level-latest');
    const latestLevelEntity = await this.fetchSiren(latestLevelLink.href);

    if (!latestLevelEntity?.properties?.publishedAt || latestLevelEntity.properties.publishedAt < completedAt) {
      return this._problem('No readings since work order completed');
    }

    this.dispatchEvent(new CustomEvent('complete', { detail: { href: this.href }, bubbles: true, composed: true }));
    return true;
  }

  _formatDate(date) {
    return !date ? '' : moment(date).format('MMM D');
  }

  _formatDateTime(date) {
    return !date ? '' : moment(date).format('MMM D, h:mm a');
  }
}

customElements.define('work-order-card', WorkOrderCard);
